import { AdditionalChargesCard } from '@/widgets/additional-charges-card';
import { ExpensesSummaryCard } from '@/widgets/expenses-summary-card';
import { MarginCard } from '@/widgets/facilities-margin-card';
import {
  RevenuePerBedCard,
  RevenuePerResidentCard,
} from '@/widgets/facility-revenue-kpi-cards';
import { RevenueAnalysisCard } from '@/widgets/revenue-analysis-card';
import { RevenueSummaryCard } from '@/widgets/revenue-summary-card';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';

type FinancialTabProps = {
  dateRange?: {
    from: Dayjs;
    to: Dayjs;
  };
};

/**
 * Facilities → Financial tab (ST #137).
 *
 * One component for both modes: every card resolves its own scope through
 * `useFacilityScope()`, so Portfolio and Single Mode differ only by the table.
 *
 * The date range is always whole completed months here — the header switches to
 * month pickers on this tab (see MONTHS_ONLY_TABS).
 */
export const FinancialTab: FC<FinancialTabProps> = ({ dateRange }) => {
  if (!dateRange) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Rows 1 and 2 share one 3-column grid so their vertical seams line up.
          Percentage widths cannot do this: w-2/3 + gap-4 + w-1/3 sums to more
          than the container, so flex shrinks both and the seam drifts ~5px left
          of Row 1's (card #137, Anna comment #335). */}

      {/* Row 1: Revenue | Expenses | Margin, equal thirds across the full width. */}
      <div className="grid grid-cols-3 gap-4">
        <RevenueSummaryCard dateRange={dateRange} />
        <ExpensesSummaryCard dateRange={dateRange} />
        <MarginCard dateRange={dateRange} />
      </div>

      {/* Row 2: Additional charges spans two of the same columns; the two
          per-unit KPI cards stack down the third. */}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <AdditionalChargesCard dateRange={dateRange} />
        </div>
        <div className="flex flex-col gap-4">
          <RevenuePerResidentCard dateRange={dateRange} />
          <RevenuePerBedCard dateRange={dateRange} />
        </div>
      </div>

      {/* Row 3: Revenue Analysis — ST #175. Intentionally takes no dateRange:
          that chart owns an independent 12-month window and ignores the global
          period, so wiring it here would only have to be undone. */}
      <RevenueAnalysisCard />

      {/* The facilities table is NOT rendered here. It lives below the AI
          Insight panel row in SingleFacilityTabs, so it can stay full width
          while the panel ends above it. */}
    </div>
  );
};
